import { PageTitle } from "@/components/PageTitle";
import { SuperPageServices } from "@/services/bedrock/SuperPageServices";
import { URLS, useQueryParam } from "@/utils";
import * as React from "react";
import { SuperPageUpdateDetailsForm, SuperPagePlayers, SuperPagePreview } from ".";
import { Tabs } from "@/components/ui/tabs";
import { useCallback, useEffect, useState } from "react";
import { SuperPage, SuperPageAssignment } from "@/services/bedrock";
import { toast } from "sonner";

interface Props {
  superPageId: number;
}

const SUPER_PAGE_TABS = [
  { label: "Page Details", value: "page-details", component: SuperPageUpdateDetailsForm },
  { label: "Performance & Roster", value: "performance-roster", component: SuperPagePlayers },
  { label: "Preview", value: "preview", component: SuperPagePreview },
] as const;

export const SuperPageTabs = {
  PageDetails: SUPER_PAGE_TABS[0],
  PerformanceRoster: SUPER_PAGE_TABS[1],
  Preview: SUPER_PAGE_TABS[2],
} as const;

const SuperPageTabsPage: React.FC<Props> = ({ superPageId }) => {
  const [superPage, setSuperPage] = useState<SuperPage>();
  const [selectedSuperPageAssignment, setSelectedSuperPageAssignment] = useState<SuperPageAssignment>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadSuperPage = useCallback(async () => {
    return SuperPageServices.getSuperPage(superPageId, [
      "contentPageTheme",
      "templateContentPage",
      "originalMedia",
      "videoScript",
      "categories",
      "totalViews",
      "totalClicks",
      "assignmentsOverview",
    ])
      .then(superPage => {
        setSuperPage(superPage);
      })
      .catch(error => {
        console.log(error);
        toast.error("Error getting Super Page");
      });
  }, [superPageId]);

  useEffect(() => {
    loadSuperPage();
  }, [loadSuperPage]);

  const [tab, setTab] = useQueryParam(
    "tab",
    SUPER_PAGE_TABS[0].value,
    SUPER_PAGE_TABS.map(t => t.value)
  );

  const handlePreviewAssignment = useCallback((superPageAssignment: SuperPageAssignment) => {
    setSelectedSuperPageAssignment(superPageAssignment);
    setTab(SuperPageTabs.Preview.value);
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <PageTitle
        title="Update Super Page"
        hideSeparator={true}
        breadcrumbs={[
          { name: "Library", link: URLS.coach.videoPages },
          { name: "Super Pages", link: URLS.coach.superPages },
        ]}
      />

      {superPage && (
        <Tabs value={tab} onValueChange={setTab}>
          <Tabs.List className="mb-4" variant="menu">
            {SUPER_PAGE_TABS.map(tab => (
              <Tabs.Trigger key={tab.value} value={tab.value} disabled={isSubmitting}>
                {tab.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          {SUPER_PAGE_TABS.map(tab => (
            <Tabs.Content key={tab.value} value={tab.value}>
              <tab.component
                superPage={superPage}
                superPageAssignment={selectedSuperPageAssignment}
                onSubmittingChange={setIsSubmitting}
                onSuperPageUpdated={loadSuperPage}
                onPreviewAssignment={handlePreviewAssignment}
                onGoToPageDetails={() => setTab(SuperPageTabs.PageDetails.value)}
              />
            </Tabs.Content>
          ))}
        </Tabs>
      )}
    </div>
  );
};

export { SuperPageTabsPage };
